/**
 * Image Primitive
 * Semantic image component with aspect ratios, loading states, and design system tokens
 */

import React, { useState } from "react";
import { Image as ChakraImage, Box, Skeleton } from "@chakra-ui/react";
import { SizeToken } from "../foundation";
import { Color } from "../foundation/Color";
import { Radius } from "../foundation/Radius";
import { useColorModeValue } from "../hooks/useColorModeValue";

// Branded types for image tokens
declare const __image_fit_brand: unique symbol;
declare const __image_radius_brand: unique symbol;
declare const __image_aspect_brand: unique symbol;

export interface ImageFitToken {
  readonly [__image_fit_brand]: true;
  readonly _value: string;
}

export interface ImageRadiusToken {
  readonly [__image_radius_brand]: true;
  readonly _value: string;
}

export interface ImageAspectToken {
  readonly [__image_aspect_brand]: true;
  readonly _value: string;
}

// Factory functions
const createImageFitToken = (value: string): ImageFitToken => ({ _value: value } as ImageFitToken);
const createImageRadiusToken = (value: string): ImageRadiusToken => ({ _value: value } as ImageRadiusToken);
const createImageAspectToken = (value: string): ImageAspectToken => ({ _value: value } as ImageAspectToken);

export interface ImageProps {
  src: string;
  alt: string;
  aspectRatio?: ImageAspectToken;
  fit?: ImageFitToken;
  radius?: ImageRadiusToken;
  width?: SizeToken;
  height?: SizeToken;
  loading?: "eager" | "lazy";
  fallback?: React.ReactNode;
  showSkeleton?: boolean;
  onLoad?: () => void;
  onError?: () => void;
  "data-testid"?: string;
}

function ImageComponent({
  src,
  alt,
  aspectRatio,
  fit,
  radius,
  width,
  height,
  loading = "lazy",
  fallback,
  showSkeleton = true,
  onLoad,
  onError,
  "data-testid": testId,
}: ImageProps) {
  // Set defaults inside the component to avoid circular reference issues
  const resolvedFit = fit || imageTokens.fit.cover;
  const resolvedRadius = radius || imageTokens.radius.none;

  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Get aspect ratio value
  const getAspectRatio = (aspect?: ImageAspectToken) => {
    if (!aspect) return undefined;

    switch (aspect._value) {
      case "square":
        return 1;
      case "16/9":
        return 16 / 9;
      case "4/3":
        return 4 / 3;
      case "3/2":
        return 3 / 2;
      case "2/1":
        return 2 / 1;
      default:
        return undefined;
    }
  };

  // Get object fit value
  const getObjectFit = (fit: ImageFitToken) => fit._value as "cover" | "contain" | "fill" | "none" | "scale-down";

  // Get border radius value
  const getBorderRadius = (radius: ImageRadiusToken) => {
    switch (radius._value) {
      case "tiny":
        return Radius.tiny._value;
      case "small":
        return Radius.small._value;
      case "medium":
        return Radius.medium._value;
      case "large":
        return Radius.large._value;
      case "extraLarge":
        return Radius.extraLarge._value;
      case "full":
        return "50%";
      case "none":
      default:
        return "0";
    }
  };

  const handleLoad = () => {
    setIsLoading(false);
    onLoad?.();
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    onError?.();
  };

  const aspectRatioValue = getAspectRatio(aspectRatio);
  const objectFit = getObjectFit(resolvedFit);
  const borderRadius = getBorderRadius(resolvedRadius);

  // Show fallback if there's an error
  if (hasError && fallback) {
    return <>{fallback}</>;
  }

  // Show skeleton while loading if enabled
  if (isLoading && showSkeleton) {
    return (
      <Skeleton
        width={width?._value}
        height={height?._value}
        aspectRatio={aspectRatioValue}
        borderRadius={borderRadius}
        data-testid={testId ? `${testId}-skeleton` : undefined}
      />
    );
  }

  return (
    <Box
      width={width?._value}
      height={height?._value}
      aspectRatio={aspectRatioValue}
      overflow="hidden"
      borderRadius={borderRadius}
      bg={useColorModeValue(Color.surface.secondary)}
      data-testid={testId}
    >
      <ChakraImage
        src={src}
        alt={alt}
        width="100%"
        height="100%"
        objectFit={objectFit}
        loading={loading}
        onLoad={handleLoad}
        onError={handleError}
        fallback={fallback}
      />
    </Box>
  );
}

// Image tokens
const imageTokens = {
  fit: {
    cover: createImageFitToken("cover"),
    contain: createImageFitToken("contain"),
    fill: createImageFitToken("fill"),
    none: createImageFitToken("none"),
    scaleDown: createImageFitToken("scale-down"),
  } as const,

  radius: {
    none: createImageRadiusToken("none"),
    tiny: createImageRadiusToken("tiny"),
    small: createImageRadiusToken("small"),
    medium: createImageRadiusToken("medium"),
    large: createImageRadiusToken("large"),
    extraLarge: createImageRadiusToken("extraLarge"),
    full: createImageRadiusToken("full"),
  } as const,

  aspect: {
    square: createImageAspectToken("square"),
    video: createImageAspectToken("16/9"),
    photo: createImageAspectToken("4/3"),
    landscape: createImageAspectToken("3/2"),
    banner: createImageAspectToken("2/1"),
  } as const,
};

// Public API - combines tokens and component
export const Image = Object.assign(ImageComponent, imageTokens);
